import React, { useEffect, useState } from 'react';
import { CalendarDays, Users, Calendar, X, Check, Star, Clock3 } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import PeriodNav from '../ui/PeriodNav';
import UploadControls from './UploadControls';
import { apiFetch } from '../../services/apiClient';
import { toast } from '../Toast';
import { leaveLabel } from '../../services/leave';
import PageHeader from '../ui/PageHeader';
import ControlGroup from '../ui/ControlGroup';
import { ControlBar, ToggleGroup, SearchBox, EndGroup } from '../ui/ControlBar';

// Helper to get YYYY-MM-DD in local time
const toLocalISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const LEAVE_TYPES = ['Annual Leave', 'Training Leave', 'Off-In-Lieu', 'Public Holiday Quota', 'Maternity Leave'];

const LeaveModal = ({ isOpen, onClose, data, onConfirm, mode }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  // Per-interval leave label. Combobox: preset types via datalist + free-text.
  const [leaveLabel, setLeaveLabel] = useState('');
  React.useEffect(() => { setLeaveLabel((data && data.label) || ''); }, [data]);
  if (!isOpen || !data) return null;
  
  const handleAction = async (subType) => {
    setIsProcessing(true);
    await onConfirm({ ...data, subType, label: leaveLabel.trim() });
    setIsProcessing(false);
    onClose();
  };

  const isRemoving = data.isRemoving;
  const isWishlist = mode === 'wishlist';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-slate-900 tracking-tight">
              {isWishlist ? 'On-Call Request' : (isRemoving ? 'Remove Leave' : 'Apply Leave')}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          
          <div className="space-y-5 mb-8">
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center border border-slate-100">
                {isWishlist ? <Star className="w-4 h-4 text-[var(--primary)]" /> : <Users className="w-4 h-4 text-slate-500" />}
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Staff Member</p>
                <p className="text-sm font-bold text-slate-700">{data.empName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center border border-slate-100"><Calendar className="w-4 h-4 text-slate-500" /></div>
              <div className="flex-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Duration</p>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">From</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(data.startRaw).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} <span className="text-[10px] text-[var(--primary)] font-bold uppercase ml-1">{data.startPeriod}</span></span>
                  </div>
                  <div className="h-px bg-slate-200/50 w-full" />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">To</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(data.endRaw).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} <span className="text-[10px] text-[var(--primary)] font-bold uppercase ml-1">{data.endPeriod}</span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!isWishlist && !isRemoving && (
            <div className="mb-6">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Leave Type</p>
              <input
                type="text"
                list="leave-type-options"
                value={leaveLabel}
                onChange={(e) => setLeaveLabel(e.target.value)}
                placeholder="Select or type a leave type"
                className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:border-[var(--primary)] focus:bg-white transition-all outline-none"
              />
              <datalist id="leave-type-options">
                {LEAVE_TYPES.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {isWishlist ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleAction('PREFER')} isLoading={isProcessing} variant="primary" icon={Check} className="flex-1">Preference</Button>
                  <Button onClick={() => handleAction('FORBID')} isLoading={isProcessing} variant="secondary" icon={X} className="flex-1 border-[var(--primary)]/20 text-[var(--primary)] hover:bg-[var(--primary)]/5">No-Call</Button>
                </div>

                <Button onClick={() => handleAction('REMOVE')} variant="ghost" className="w-full text-slate-400 text-xs font-bold">Clear All Requests</Button>
              </>
            ) : (
              <div className="flex gap-3">
                <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
                <Button onClick={() => handleAction(isRemoving ? 'REMOVE' : 'POST')} isLoading={isProcessing} variant="primary" className="flex-1" icon={Check}>Confirm</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LeaveRosterTab = ({ schedule, loading, solving, selectedWeek, setSelectedWeek, timeView, setTimeView, displayDates, isEmployeeUnavailable, getEmployeeColor, isCellSelected, setSelectionStart, setSelectionEnd, setIsDraggingSelection, isDraggingSelection, selectionStart, selectionEnd, downloadTemplate, handleFileUpload, fileInputRef, loadDemoData }) => {

  // Weekend tinting, matching the Final Roster and Management Console.
  // Sat/Sun text stays slate like every other day -- the grey column is what
  // marks the weekend now, so a second colour signal is redundant.
  const WEEKEND_HEADER = 'bg-slate-200';
  const WEEKEND_CELL = 'bg-slate-100';
  const [modal, setModal] = useState({ isOpen: false, data: null });
  const [selectionMode, setSelectionMode] = useState('leave'); // 'leave' | 'wishlist'
  const [staffQuery, setStaffQuery] = useState('');

  const handleConfirmAction = async (data) => {
    const isWishlist = selectionMode === 'wishlist';
    let method = 'POST';
    let endpoint = isWishlist ? '/api/on-call/preference' : '/api/leave';

    if (data.subType === 'REMOVE') {
      method = 'DELETE';
    } else if (data.subType === 'FORBID') {
      endpoint = '/api/on-call/forbidden';
    }

    try {
      const payload = {
        staff_id: data.empId,
        start_date: data.startRaw,
        end_date: data.endRaw
      };

      if (!isWishlist) {
        payload.start_period = data.startPeriod;
        payload.end_period = data.endPeriod;
        // Per-interval leave label (e.g. "Annual Leave"). Display-only.
        if (data.label) payload.label = data.label;
      }

      if (isWishlist && data.subType === 'REMOVE') {
        await apiFetch('/api/on-call/preference', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        await apiFetch('/api/on-call/forbidden', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        const res = await apiFetch(endpoint, {
          method: method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        // The server can legitimately refuse — starring an on-call date the
        // person is on leave for is rejected rather than silently deleting the
        // leave. Without surfacing the reason the click looks like a no-op,
        // which reads as a bug.
        const body = await res.json().catch(() => null);
        if (body && body.success === false) {
          toast.error(body.error || 'That change could not be applied.');
          return;
        }
      }
      loadDemoData('SMALL');
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingSelection && selectionStart && selectionEnd) {
        let isRemoving = false;
        if (selectionMode === 'leave') {
          isRemoving = isEmployeeUnavailable(schedule, { id: selectionStart.empId }, selectionStart.date, selectionStart.period);
        }

        let s = selectionStart, e = selectionEnd;
        if (s.date.getTime() + (s.period === 'PM' ? 1 : 0) > e.date.getTime() + (e.period === 'PM' ? 1 : 0)) { [s, e] = [e, s]; }
        
        setModal({
          isOpen: true,
          data: {
            empName: s.empName,
            empId: s.empId,
            startDate: s.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            startRaw: toLocalISO(s.date),
            startPeriod: s.period,
            endDate: e.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            endRaw: toLocalISO(e.date),
            endPeriod: e.period,
            isRemoving: isRemoving
          }
        });
        setIsDraggingSelection(false); setSelectionStart(null); setSelectionEnd(null);
      } else { setIsDraggingSelection(false); }
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDraggingSelection, selectionStart, selectionEnd, schedule, isEmployeeUnavailable, selectionMode, setIsDraggingSelection, setSelectionEnd, setSelectionStart]);

  const filteredEmployees = [...(schedule?.employees || [])]
    .filter(emp => staffQuery === '' || emp.name.toLowerCase().includes(staffQuery.toLowerCase()))
    .sort((a,b) => a.name.localeCompare(b.name));

  // --- shift-type preference support (mode: 'preference') -----------------
  // Shift types by id, for code resolution.
  const shiftTypesById = React.useMemo(() => {
    const m = new Map();
    ((schedule?.shiftTypes || schedule?.shift_types) || []).forEach(t => {
      if (t && t.id) m.set(String(t.id), t);
    });
    return m;
  }, [schedule]);

  // Which shift types actually run on a given weekday, anywhere. Type-only and
  // honest: a doctor can only request a type that exists somewhere that day.
  // Derived from location_shift_patterns (day -> shift_type_id across locations).
  const typesByWeekday = React.useMemo(() => {
    const patterns = (schedule?.locationShiftPatterns || schedule?.location_shift_patterns) || {};
    const byDay = {};   // weekday(0-6) -> ordered unique type ids
    Object.values(patterns).forEach(items => {
      (items || []).forEach(it => {
        const day = typeof it.day === 'string' ? parseInt(it.day, 10) : it.day;
        const stid = String(it.shiftTypeId ?? it.shift_type_id ?? '');
        if (day == null || !stid) return;
        (byDay[day] = byDay[day] || new Set()).add(stid);
      });
    });
    // to ordered arrays: AM, PM, then others by short code
    const rank = (id) => {
      const c = String(shiftTypesById.get(id)?.shortCode || shiftTypesById.get(id)?.short_code || id).toUpperCase();
      return c === 'AM' ? [0, c] : c === 'PM' ? [1, c] : [2, c];
    };
    const out = {};
    Object.entries(byDay).forEach(([d, set]) => {
      out[d] = [...set].sort((a, b) => {
        const [ra, ca] = rank(a), [rb, cb] = rank(b);
        return ra - rb || ca.localeCompare(cb);
      });
    });
    return out;
  }, [schedule, shiftTypesById]);

  const codeOfType = React.useCallback((id) => {
    const t = shiftTypesById.get(String(id));
    return String(t?.shortCode || t?.short_code || id).toUpperCase();
  }, [shiftTypesById]);

  // (employee, dateISO, typeId) -> 'prefer' | 'avoid'
  const prefLookup = React.useMemo(() => {
    const m = new Map();
    ((schedule?.shiftPreferences || schedule?.shift_preferences) || []).forEach(pr => {
      const name = pr.staffId ?? pr.staff_id;            // the person's id, never a name
      const date = String(pr.date || '').split('T')[0];
      const stid = String(pr.shiftTypeId ?? pr.shift_type_id ?? '');
      const dir = String(pr.direction || '').toLowerCase();
      if (name && date && stid && (dir === 'prefer' || dir === 'avoid')) {
        m.set(`${name}::${date}::${stid}`, dir);
      }
    });
    return m;
  }, [schedule]);

  const [prefBusy, setPrefBusy] = React.useState(false);

  // Neutral -> Prefer -> Avoid -> Neutral. Persists via the endpoint; the local
  // schedule is the source of truth on refetch, so we optimistically no-op and
  // rely on the parent reload (loadDemoData) to reflect the saved state.
  const cyclePreference = React.useCallback(async (staffId, dateISO, typeId, current) => {
    if (prefBusy) return;
    const next = current === undefined ? 'prefer' : current === 'prefer' ? 'avoid' : 'neutral';
    setPrefBusy(true);
    try {
      const res = await apiFetch('/api/shift-preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_id: staffId, date: dateISO, shift_type_id: typeId, direction: next }),
      });
      // SAY SO when the server refuses. The response was previously discarded,
      // so a rejected preference looked identical to a saved one until the
      // reload silently put the cell back -- and the reason never reached the
      // person who could fix it.
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.success === false) {
        toast.error(body.message || 'That preference could not be saved.');
        return;
      }
      if (typeof loadDemoData === 'function') await loadDemoData();
    } catch (e) {
      console.error('Failed to set shift preference', e);
      toast.error('That preference could not be saved.');
    } finally {
      setPrefBusy(false);
    }
  }, [prefBusy, loadDemoData]);

  return (
    <div className="animate-in fade-in space-y-6">
      <LeaveModal isOpen={modal.isOpen} onClose={() => setModal({ ...modal, isOpen: false })} data={modal.data} onConfirm={handleConfirmAction} mode={selectionMode} />
      
      {/* SECTION HEADER */}
      <PageHeader icon={CalendarDays} title="Availability & Wishlist" />

      {/* UNIFIED CONTROL CONTAINER — one wrapping row (Sep 2026),
          standardised with the Final Roster and Management Console:
          [mode] [period] [search] ..................... [upload ▾].
          The Weekly/Monthly choice lives in the period navigator's label menu,
          matching Final Roster's Week/Fortnight. */}
      <ControlBar>
        <ToggleGroup label="Mode" value={selectionMode} onChange={setSelectionMode}
          options={[
            { id: 'leave', label: 'Leave', icon: CalendarDays },
            { id: 'wishlist', label: 'Call', icon: Star },
            { id: 'preference', label: 'Shifts', icon: Clock3 },
          ]} />
        <ControlGroup label="Period" className="shrink-0">
          {/* selectedWeek is an OFFSET in whatever unit the view uses:
              App.getWeekDates reads it as weeks in 'weekly' and as MONTHS in
              'monthly', so stepping by 1 is correct in both. Switching views
              resets the offset — "3 weeks ahead" must not silently become
              "3 months ahead". */}
          <PeriodNav
            startDate={displayDates?.[0]}
            endDate={displayDates?.[displayDates.length - 1]}
            label={timeView === 'monthly'
              ? (displayDates?.[0]
                  ? displayDates[0].toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
                  : `Mo ${selectedWeek + 1}`)
              : undefined}
            onPrev={() => setSelectedWeek(selectedWeek - 1)}
            onNext={() => setSelectedWeek(selectedWeek + 1)}
            menu={[
              { id: 'weekly', label: 'Weekly view', sublabel: '7 days',
                checked: timeView === 'weekly',
                onSelect: () => { setTimeView('weekly'); setSelectedWeek(0); } },
              { id: 'monthly', label: 'Monthly view', sublabel: 'Calendar month',
                checked: timeView === 'monthly',
                onSelect: () => { setTimeView('monthly'); setSelectedWeek(0); } },
            ]}
          />
        </ControlGroup>
        <SearchBox value={staffQuery} onChange={setStaffQuery} placeholder="Search staff..." />
        <EndGroup label="Import">
            <UploadControls
              templateType="leaves"
              downloadTemplate={downloadTemplate}
              handleFileUpload={handleFileUpload}
              fileInputRef={fileInputRef}
              disabled={loading || solving}
              variant="primary"
            />
        </EndGroup>
      </ControlBar>

      {!schedule ? (
        <Card className="py-24 text-center"><CalendarDays className="w-16 h-16 text-slate-200 mx-auto mb-4" /><h2 className="text-xl font-bold text-slate-700 mb-2">No Schedule Loaded</h2></Card>
      ) : (
        <div className="bg-white rounded-[20px] shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse table-fixed ${timeView === 'monthly' ? 'min-w-[1400px]' : ''}`}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] w-48 border-r border-slate-200 sticky left-0 z-20 bg-slate-50">Employee</th>
                  {displayDates.map((date, i) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    return (
                      <th key={i} className={`p-2 text-center border-r border-slate-200 last:border-r-0 ${isWeekend ? WEEKEND_HEADER : 'bg-white'}`}>
                        <div className="flex flex-col items-center justify-center">
                          <div className="font-bold text-sm leading-none text-slate-800">{date.toLocaleDateString('en-GB', { weekday: timeView === 'weekly' ? 'short' : 'narrow' })}</div>
                          <div className="text-[10px] text-slate-400 font-bold mt-1.5 leading-none">{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEmployees.map(emp => (
                  <tr key={emp.name} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 bg-white border-r border-slate-200 sticky left-0 z-10 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-[6px] h-5 rounded-full" style={{ backgroundColor: getEmployeeColor(emp.name) }} />
                        <span className="font-bold text-slate-700 text-xs truncate">{emp.name}</span>
                      </div>
                    </td>
                    {displayDates.map((date, i) => {
                      const ds = toLocalISO(date);
                      const isAMLeave = isEmployeeUnavailable(schedule, emp, date, 'AM');
                      const isPMLeave = isEmployeeUnavailable(schedule, emp, date, 'PM');
                      // Leave label for this date.
                      const dayLeaveLabel = leaveLabel(emp, ds);
                      const isPreferred = (emp.onCallPreferences || emp.on_call_preferences || []).includes(ds);
                      const isForbidden = (emp.onCallForbiddenDates || emp.on_call_forbidden_dates || []).includes(ds);
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                      // --- PREFERENCE MODE: click-cycle tri-state over the
                      // shift types that actually run this weekday (honest set) ---
                      if (selectionMode === 'preference') {
                        const dayTypes = typesByWeekday[date.getDay() === 0 ? 6 : date.getDay() - 1] || [];
                        return (
                          <td key={i} className={`border-r border-slate-100 last:border-r-0 p-2 align-middle ${isWeekend ? WEEKEND_CELL : ''}`}>
                            <div className="flex justify-center w-full">
                              {dayTypes.length === 0 ? (
                                <span className="text-[8px] font-bold text-slate-200 uppercase tracking-widest">—</span>
                              ) : (
                                <div className="flex w-full gap-1 flex-wrap justify-center">
                                  {dayTypes.map(typeId => {
                                    const code = codeOfType(typeId);
                                    const dir = prefLookup.get(`${emp.id}::${ds}::${typeId}`);
                                    let cls = 'bg-slate-50 border-slate-100 text-slate-400';
                                    if (dir === 'prefer') cls = 'bg-emerald-100 border-emerald-300 text-emerald-700 shadow-sm';
                                    else if (dir === 'avoid') cls = 'bg-amber-100 border-amber-300 text-amber-700 shadow-sm';
                                    return (
                                      <button
                                        key={typeId}
                                        type="button"
                                        disabled={prefBusy}
                                        onClick={() => cyclePreference(emp.id, ds, typeId, dir)}
                                        title={`${code}: ${dir || 'no preference'} — click to cycle`}
                                        className={`flex items-center justify-center h-9 min-w-[30px] px-1.5 text-[9px] font-bold rounded-md border-2 transition-all cursor-pointer select-none disabled:opacity-50 ${cls}`}
                                      >
                                        <span className="flex flex-col items-center leading-none gap-0.5">
                                          <span className="tracking-tighter">{code}</span>
                                          {dir === 'prefer' && <Check className="w-2.5 h-2.5" />}
                                          {dir === 'avoid' && <X className="w-2.5 h-2.5 stroke-[3px]" />}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td key={i} className={`border-r border-slate-100 last:border-r-0 p-2 align-middle ${isWeekend ? WEEKEND_CELL : ''}`}>
                          <div className="flex justify-center w-full">
                            <div className={`${timeView === 'monthly' ? 'grid grid-cols-2 w-[100px] h-9' : 'flex w-full h-10'} gap-1.5`}>
                              {[ 'AM', 'PM' ].map(period => {
                                const isLeave = period === 'AM' ? isAMLeave : isPMLeave;
                                const selected = isCellSelected(emp.id, i, period, displayDates);
                                
                                let cellStyle = "bg-slate-50 border-slate-100 text-slate-300";
                                let content = timeView === 'monthly' ? period[0] : period;
                                const fontSize = timeView === 'monthly' ? 'text-[9px]' : 'text-[8px]';

                                if (selected) {
                                  cellStyle = "bg-[var(--primary)]/20 border-[var(--primary)] shadow-sm text-[var(--primary)]";
                                } else if (isLeave) {
                                  cellStyle = "bg-rose-100 border-rose-200 text-rose-700 shadow-sm";
                                  // Visual indication of the label: show it (weekly) with a
                                  // tooltip; monthly stays compact ("L") with the label in the title.
                                  content = (
                                    <span className={`${fontSize} font-bold tracking-tighter truncate max-w-full px-0.5`} title={dayLeaveLabel ? `${dayLeaveLabel} (${period})` : `Leave (${period})`}>
                                      {timeView === 'monthly' ? 'L' : (dayLeaveLabel || 'LEAVE')}
                                    </span>
                                  );
                                } else if (isPreferred) {
                                  cellStyle = "bg-orange-100 border-orange-200 text-orange-700 shadow-sm";
                                  content = (
                                    <div className="flex flex-col items-center leading-none gap-0.5 min-w-0">
                                      <Check className={`${timeView === 'monthly' ? 'w-3 h-3' : 'w-2.5 h-2.5'}`} />
                                      <span className={`${fontSize} font-bold tracking-tighter`}>{timeView === 'monthly' ? 'Y' : 'CALL'}</span>
                                    </div>
                                  );
                                } else if (isForbidden) {
                                  cellStyle = "bg-rose-100 border-rose-200 text-rose-700 shadow-sm";
                                  content = (
                                    <div className="flex flex-col items-center leading-none gap-0.5 min-w-0">
                                      <X className={`${timeView === 'monthly' ? 'w-3 h-3' : 'w-2.5 h-2.5'} stroke-[3px]`} />
                                      <span className={`${fontSize} font-bold tracking-tighter`}>{timeView === 'monthly' ? 'N' : 'CALL'}</span>
                                    </div>
                                  );
                                }

                                return (
                                  <div 
                                    key={period}
                                    onMouseDown={() => { setSelectionStart({ empName: emp.name, empId: emp.id, dateIdx: i, period, date }); setSelectionEnd({ empName: emp.name, empId: emp.id, dateIdx: i, period, date }); setIsDraggingSelection(true); }}
                                    onMouseEnter={() => { if (isDraggingSelection && selectionStart) setSelectionEnd({ empName: emp.name, empId: emp.id, dateIdx: i, period, date }); }}
                                    className={`flex items-center justify-center ${fontSize} font-bold rounded-md transition-all cursor-pointer select-none border-2 overflow-hidden ${cellStyle} ${timeView === 'monthly' ? '' : 'flex-1'}`}
                                  >
                                    {content}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRosterTab;
